import React from 'react';
import { type ComponentSize } from 'src/modules/shared/types/component.type';
import { PiWarningCircle } from 'react-icons/pi';

interface MediaErrorSkeletonProps {
  className?: string;
  size?: ComponentSize;
  type?: 'image' | 'video' | 'audio' | 'file';
}

export const MediaErrorSkeleton: React.FC<MediaErrorSkeletonProps> = ({
  className = '',
  size = 'md',
  type = 'image',
}) => {
  const sizeClasses: Record<ComponentSize, Record<string, string>> = {
    '2xs': {
      image: 'min-h-[100px] min-w-[75px] aspect-[3/4]',
      video: 'min-h-[100px] min-w-[75px] aspect-[3/4]',
      audio: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
      file: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
    },
    xs: {
      image: 'min-h-[200px] min-w-[150px] aspect-[3/4]',
      video: 'min-h-[200px] min-w-[150px] aspect-[3/4]',
      audio: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
      file: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
    },
    sm: {
      image: 'min-h-[300px] min-w-[225px] aspect-[3/4]',
      video: 'min-h-[300px] min-w-[225px] aspect-[3/4]',
      audio: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
      file: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
    },
    md: {
      image: 'min-h-[400px] min-w-[300px] aspect-[3/4]',
      video: 'min-h-[400px] min-w-[300px] aspect-[3/4]',
      audio: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
      file: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
    },
    lg: {
      image: 'min-h-[500px] min-w-[375px] aspect-[3/4]',
      video: 'min-h-[500px] min-w-[375px] aspect-[3/4]',
      file: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
      audio: 'min-h-[48px] min-w-[200px] aspect-[5/1]',
    },
  };

  const iconSizeClasses: Record<ComponentSize, Record<string, string>> = {
    '2xs': {
      image: 'w-6 h-6',
      video: 'w-6 h-6',
      audio: 'w-5 h-5',
      file: 'w-5 h-5',
    },
    xs: {
      image: 'w-8 h-8',
      video: 'w-8 h-8',
      audio: 'w-5 h-5',
      file: 'w-5 h-5',
    },
    sm: {
      image: 'w-10 h-10',
      video: 'w-10 h-10',
      audio: 'w-5 h-5',
      file: 'w-5 h-5',
    },
    md: {
      image: 'w-12 h-12',
      video: 'w-12 h-12',
      audio: 'w-5 h-5',
      file: 'w-5 h-5',
    },
    lg: {
      image: 'w-16 h-16',
      video: 'w-16 h-16',
      audio: 'w-5 h-5',
      file: 'w-5 h-5',
    },
  };

  return (
    <div
      className={`
        rounded-lg w-full h-full relative
        bg-secondary-200
        ${sizeClasses[size][type]}
        ${className}
      `.trim()}
    >
      <div className="absolute inset-0 flex items-center justify-center translate-x-2">
        <PiWarningCircle
          className={`${iconSizeClasses[size].image} text-neutral-200`}
        />
      </div>
    </div>
  );
};
